/** Dashboard page — recent jobs, quick-run plugins, system stats (FR-SPA-03). */

import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Grid2 as Grid, Paper, Card, CardActionArea, CardContent,
  CircularProgress, List, ListItemButton, ListItemText,
} from "@mui/material";
import { useAuth } from "@/hooks/useAuth";
import { useFeatureGate } from "@/hooks/useFeatureGate";
import { useDashboard } from "@/hooks/useDashboard";
import StatusChip from "@/components/StatusChip";

export default function Dashboard() {
  const { user } = useAuth();
  const { hasAccess: isAdmin } = useFeatureGate("admin");
  const { data, isLoading } = useDashboard();
  const navigate = useNavigate();

  if (isLoading) return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>;

  const recentJobs = data?.recentJobs ?? [];
  const topPlugins = data?.topPlugins ?? [];
  const stats = data?.systemStats;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Welcome{user?.profile?.given_name ? `, ${user.profile.given_name}` : ""}
      </Typography>

      <Grid container spacing={3}>
        {/* Recent Jobs */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Recent Jobs</Typography>
            {recentJobs.length === 0 ? (
              <Typography color="text.secondary">No recent jobs</Typography>
            ) : (
              <List disablePadding>
                {recentJobs.map((job) => (
                  <ListItemButton key={job.jobId} onClick={() => navigate(`/jobs/${job.jobId}`)} dense>
                    <ListItemText
                      primary={job.pluginName ?? job.pluginId}
                      secondary={new Date(job.createdAt).toLocaleString()}
                    />
                    <StatusChip status={job.status} />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Quick Run */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Quick Run</Typography>
            {topPlugins.length === 0 ? (
              <Typography color="text.secondary">Run some jobs to see shortcuts here</Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {topPlugins.map((pid) => (
                  <Card key={pid} variant="outlined">
                    <CardActionArea onClick={() => navigate(`/plugins/${pid}`)}>
                      <CardContent sx={{ py: 1 }}>
                        <Typography variant="body2">{pid}</Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* System Stats (admin only) */}
        {isAdmin && stats && (
          <Grid size={12}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>System Status</Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="h4" color="primary">{stats.activePlugins}</Typography>
                    <Typography variant="body2" color="text.secondary">Active Plugins</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="h4" color="info.main">{stats.pendingJobs}</Typography>
                    <Typography variant="body2" color="text.secondary">Pending Jobs</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="h4" color="error">{stats.failedJobs}</Typography>
                    <Typography variant="body2" color="text.secondary">Failed Jobs</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="h4" color="success.main">{stats.activeSchedules}</Typography>
                    <Typography variant="body2" color="text.secondary">Active Schedules</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
